import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Box } from '@mui/material';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useInsuranceApi } from '../../../../../hooks/use-insurance-api';
import { downloadFile } from '../../../../../utils/file/download-file';
import { ButtonArea } from '../../../../shared/button-area/button-area';
import { CloseButton, DialogSubTitle, DialogToolbar } from '../../../../shared/dialog/dialog.styles';
import { VerifiedInsuranceWarning } from '../../../shared/components/verified-insurance-warning/verified-insurance-warning';
import { InsuranceVerifiedStatus, StatusText, StatusWrapper } from '../../../shared/styles';
import { withPatientIvStatusDialogProvider } from '../../hooks/use-patient-iv-status-dialog/hoc';
import { usePatientIvStatusDialog } from '../../hooks/use-patient-iv-status-dialog/hook';
import { PatientIvStatusDialog } from '../patient-iv-status-dialog';
import { AppointmentEndedMessage } from '../shared/appointment-ended-message/appointment-ended-message';
import { ManuallyVerifiedButton } from '../shared/manually-verified-button';
import { RunVerificationButton } from '../shared/run-verification-button';
import { SelfPayButton } from '../shared/self-pay-button';
import { BoldText } from '../styles';
import { DateOfBirth, SubTitleWrapper } from './styles';

const InsuranceVerifiedStatusDialog: FC = () => {
  const { status, getVerificationDocumentUrl } = useInsuranceApi();

  const { opened, onClose, isLoading, locationId, selectedIvSummary, patientInfo, getCarrierSummary, appointment } =
    usePatientIvStatusDialog();

  const [verificationPdfUrl, setVerificationPdfUrl] = useState<string>();
  const [carrierSummary] = useState(getCarrierSummary(selectedIvSummary?.idCarrierName));

  useEffect(() => {
    if (!opened || !selectedIvSummary) return;

    (async () => {
      const docUrl = await getVerificationDocumentUrl(locationId, selectedIvSummary.idVerificationRequest);

      setVerificationPdfUrl(docUrl ?? undefined);
    })();
  }, [opened, locationId, selectedIvSummary]);

  const downloadVerificationPdf = async () => {
    if (!verificationPdfUrl) return;

    downloadFile(verificationPdfUrl, 'Verification.pdf');
  };

  return (
    <PatientIvStatusDialog
      opened={opened}
      title={`${patientInfo?.firstName} ${patientInfo?.lastName}`}
      subtitle={
        <SubTitleWrapper>
          <DateOfBirth>
            <BoldText>Date of Birth</BoldText>
            <Box>{patientInfo?.dateOfBirth ? moment(patientInfo.dateOfBirth).format('MMMM D, YYYY') : 'Unknown'}</Box>
          </DateOfBirth>
          <StatusWrapper>
            <InsuranceVerifiedStatus />
            <StatusText>Insurance Verified</StatusText>
          </StatusWrapper>
        </SubTitleWrapper>
      }
    >
      <DialogSubTitle sx={{ margin: '30px 0 20px 0', display: 'block' }}>
        The patient's insurance has been <BoldText>successfully verified.</BoldText> You can download the available
        verification form below.
      </DialogSubTitle>
      <AppointmentEndedMessage />
      <ButtonArea
        isLoading={isLoading || status.getVerificationDocumentUrl.isRunning}
        buttons={[
          {
            title: 'Download Verification PDF',
            onClick: downloadVerificationPdf,
            disabled: !verificationPdfUrl,
            endIcon: <DownloadOutlinedIcon />
          }
        ]}
      />

      <VerifiedInsuranceWarning
        locationId={locationId}
        carrierNameId={selectedIvSummary?.idCarrierName}
        rwErrorCode={selectedIvSummary?.rwErrorCode}
        websiteErrorCode={selectedIvSummary?.websiteErrorCode}
        connectionStatus={carrierSummary?.connectionStatus}
        ediErrorMessage={selectedIvSummary?.ediErrorMessage}
      />

      <DialogToolbar>
        <ManuallyVerifiedButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <SelfPayButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <RunVerificationButton
          label="Re-run Verification"
          onSuccess={async () => onClose()}
          disabled={isLoading || appointment?.isEnded}
        />
        <CloseButton onClick={onClose} disabled={isLoading} />
      </DialogToolbar>
    </PatientIvStatusDialog>
  );
};

export default withPatientIvStatusDialogProvider(InsuranceVerifiedStatusDialog);
